'use client';

import { useState } from 'react';
import Image from 'next/image';

import { useMedia } from 'react-use';

import { Icon } from '~/components/ui/icon';
import { cn } from '~/lib/utils';
import { BlockImagesFragment } from '~/server/generated/contentful-graphql';

import { BlockFooterCTA } from './block-footer-cta';
import { SectionHeader } from './section-header';

export function Images({ block }: { block: BlockImagesFragment }) {
  const {
    imagesCollection,
    separator,
    headline,
    icon,
    ctaHeadline,
    ctaLink,
    ctaLinkAsset,
    ctaLinkContentCollection,
    ctaTitle,
  } = block;

  const isDesktop = useMedia('(min-width: 1024px)', false);

  const [activeSlide, setActiveSlide] = useState(0);

  const images = imagesCollection?.items.flatMap((item) => item ?? []) ?? [];
  const singleImage = images.length === 1;
  const isSlideshow = isDesktop && images.length > 3;

  const handleClick = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveSlide(activeSlide - 1);
    }

    if (direction === 'next' && activeSlide !== images.length - 1) {
      setActiveSlide(activeSlide + 1);
    }
  };

  return (
    <section
      className={cn('container', separator === 'Default' && 'separator', isSlideshow ? 'max-w-4xl' : 'xl:max-w-7xl')}
    >
      <SectionHeader heading={headline} icon={icon} />
      {isSlideshow ? (
        <div className='grid grid-cols-[32px_1fr_32px] items-center gap-6'>
          <button
            className='bg-ultramarine size-10 rounded-full p-2 text-white disabled:opacity-50'
            disabled={activeSlide === 0}
            onClick={() => handleClick('prev')}
          >
            <Icon use='chevron-left' className='size-6' />
          </button>
          <div className='grid'>
            {images.map((image, index) => (
              <figure
                className={cn(
                  `col-start-1 row-start-1 transition-opacity ${activeSlide !== index ? 'opacity-0' : 'opacity-100'}`,
                )}
                key={image.sys.id}
              >
                {image.url && (
                  <Image
                    alt={image?.title || ''}
                    className='h-auto max-w-full rounded-2xl'
                    height={511}
                    src={`${image.url}?fit=fill&w=1536&h=1023`}
                    width={768}
                  />
                )}
                {image.description && (
                  <figcaption className='body-sm text-muted-foreground mx-auto mt-3 max-w-2xl'>
                    {image.description}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          <button
            className='bg-ultramarine size-10 rounded-full p-2 text-white disabled:opacity-50'
            disabled={activeSlide === images.length - 1}
            onClick={() => handleClick('next')}
          >
            <Icon use='chevron-right' className='size-6' />
          </button>
        </div>
      ) : (
        <>
          <div
            className={cn(
              'text-center',
              !singleImage &&
                'hide-scrollbar flex gap-5 max-sm:-mx-5 max-sm:overflow-x-auto max-sm:px-5 lg:mx-0 lg:gap-8',
            )}
          >
            {images.map((image) => (
              <figure className={cn(!singleImage && 'max-sm:w-[75vw] max-sm:shrink-0')} key={image.sys.id}>
                {image.url && (
                  <Image
                    alt={image?.title || ''}
                    className='mx-auto h-auto max-w-full rounded-2xl'
                    height={511}
                    src={`${image.url}?fit=fill&w=1536&h=1023`}
                    width={768}
                  />
                )}
                {image.description && (
                  <figcaption className='body-sm text-muted-foreground mx-auto mt-3 max-w-2xl'>
                    {image.description}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </>
      )}
      <BlockFooterCTA
        ctaHeadline={ctaHeadline}
        ctaLink={ctaLink}
        ctaLinkAssetUrl={ctaLinkAsset?.url}
        ctaLinkContentSlug={ctaLinkContentCollection?.items[0]?.slug}
        ctaLinkContentType={ctaLinkContentCollection?.items[0]?.__typename}
        ctaTitle={ctaTitle}
      />
    </section>
  );
}
